import * as THREE from 'three';
import { C, SEASON_LEAF } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { checkNpcClick } from '../entities/townsfolk.js';
import { makeDowntownCrowd } from '../entities/npc.js';
import { createAmbient } from '../entities/ambient.js';
import { WeatherSystem, getWeatherState, weatherName, weatherIcon } from '../core/weather.js';
import { getSeason, isNight } from '../core/world.js';
import { HUD } from '../ui/hud.js';

// Uptown Toast Town — the posh district north of the plaza.
// Rice Boi's Dojo, the Casino, the Upper Crust Apartments, the Garage, and the
// Space Center that launches the Space Bread Program.
//
// Uptown's silhouette is FORMAL: a squared, paved plaza on a strict axis, with the
// four frontages set on the diagonals and the north–south axis left clear for the
// Space Center and the road home. Nothing here is scattered; it is the expensive
// part of town and it should look composed.
//
// It used to clamp you to ±11 inside a radius-26 disc — you could walk barely the
// middle third of what you could see. Now the ground runs to 34, you walk to 26, and
// a stone balustrade with a clipped hedge behind it stands exactly on that line.

export function createUptownScene(){
  return () => new UptownScene();
}

const GROUND_R = 34;      // how far the lawn is drawn
const WALK_R   = 26;      // how far you may walk — the balustrade stands here
const PLAZA    = 12;      // half-width of the paved square
const FOUNTAIN_R = 3.6;   // you may not wade in

const LAMP_ON  = 0xFFE9A8;
const LAMP_OFF = 0xD8D2C2;

// The four frontages, on the diagonals so the N–S axis stays clear. Their colours are
// each building's identity (red dojo, purple casino, gold apartments, brown garage) —
// everything that ISN'T a building is drawn from core/palette.js.
const BUILDINGS = [
  { id: 'dojo',      name: "Rice Boi's Dojo",           scene: 'dojo',      color: 0xC94F4F, pos: new THREE.Vector3(-13, 0, -12), tower: false },
  { id: 'casino',    name: 'Lucky Crumb Casino',        scene: 'casino',    color: 0x5A3D8C, pos: new THREE.Vector3( 13, 0, -12), tower: false },
  { id: 'apartment', name: 'The Upper Crust Apartments', scene: 'apartment', color: 0xD9A441, pos: new THREE.Vector3(-13, 0,  12), tower: true  },
  { id: 'garage',    name: 'Pretzel Pit Stop',          scene: 'garage',    color: 0x6E4526, pos: new THREE.Vector3( 13, 0,  12), tower: false }
];

class UptownScene {
  constructor(){
    this.panels = [];
    this.lamps = [];
    this.state = 'explore';

    this.season = getSeason();
    this.leaf = SEASON_LEAF[this.season] || C.sprout;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(C.sky);
    this.scene.fog = new THREE.Fog(C.sky, 40, 96);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 200);
    this.camDist = 20;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.52;

    this._buildLights();
    this._buildGround();
    this._buildBalustrade();
    this._buildParterres();
    this._buildFountain();
    BUILDINGS.forEach(b => this._buildBuilding(b));
    this._buildSpacePad();
    this._buildStreetFurniture();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 16);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    // a roster-driven uptown crowd. The spawner drops everyone in a narrow band, which
    // suited the old cramped plaza; spread them across this one (and move `home`, or
    // they wander back to where they were born).
    this.npcs = makeDowntownCrowd(10);
    this.npcs.forEach((n, i) => {
      const a = (i / this.npcs.length) * Math.PI * 2 + 0.4;
      const r = 7 + (i % 3) * 3.5;
      const p = new THREE.Vector3(Math.sin(a) * r, 0, Math.cos(a) * r);
      n.root.position.copy(p);
      n.home.copy(p);
      this.scene.add(n.root);
    });

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, 0), radius: 24, butterflies: 5, birds: 3, fireflies: 6 });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: WALK_R });
    this._applyWeather();

    this.hotspots = [];
    BUILDINGS.forEach(b => {
      // stand at the foot of the steps, just clear of the facade
      const door = b.pos.clone().multiplyScalar(0.79);
      this.hotspots.push({
        pos: door, r: 2.6,
        label: `enter ${b.name}`,
        action: () => this.manager.goTo(b.scene, { from: 'uptown' })
      });
    });
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, -16.5), r: 3.0, label: '🚀 Space Center — launch pad', action: () => this.manager.goTo('spaceport', { from: 'uptown' }) });
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, 24),    r: 3.0, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'uptown' }) });
    this.nearSpot = null;
    this._updateLamps();
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.72));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.88);
    sun.position.set(10, 20, 12); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 38;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    const lawn = new THREE.Mesh(new THREE.CircleGeometry(GROUND_R, 64), new THREE.MeshLambertMaterial({ color: C.grass }));
    lawn.rotation.x = -Math.PI/2; lawn.receiveShadow = true; this.scene.add(lawn);

    // the formal plaza: one square, one paving material
    const plaza = new THREE.Mesh(new THREE.PlaneGeometry(PLAZA * 2, PLAZA * 2), new THREE.MeshLambertMaterial({ color: C.path }));
    plaza.rotation.x = -Math.PI/2; plaza.position.y = 0.01; plaza.receiveShadow = true; this.scene.add(plaza);

    // A thin inlaid edging around the fountain. It was a 5.2->7.4 band, which from the
    // default camera read as a big grey pancake — a second fountain sitting behind the
    // real one. Edging, not paving.
    const ring = new THREE.Mesh(new THREE.RingGeometry(6.9, 7.4, 48), new THREE.MeshLambertMaterial({ color: C.stoneDim }));
    ring.rotation.x = -Math.PI/2; ring.position.y = 0.02; this.scene.add(ring);

    // The axis: one paved avenue, same paving as the plaza (no six-colour road
    // patchwork). It runs north as far as the Space Center apron and south THROUGH the
    // gap in the balustrade, out across the lawn — so you can see where the road goes.
    const avenue = new THREE.Mesh(new THREE.PlaneGeometry(7, 56), new THREE.MeshLambertMaterial({ color: C.path }));
    avenue.rotation.x = -Math.PI/2; avenue.position.set(0, 0.015, 2); avenue.receiveShadow = true; this.scene.add(avenue);
  }

  // The edge of the district, standing ON the walk limit: a stone balustrade with a
  // clipped hedge behind it. The gaps are the two exits, so an opening you can see
  // through is an opening you can actually use.
  _buildBalustrade(){
    const COUNT = 116;
    for (let i = 0; i < COUNT; i++){
      const a = (i / COUNT) * Math.PI * 2;
      // ONE gap, on the south axis, where the road actually leaves for town — and the
      // exit hotspot stands in it. A gap on the north axis would open onto lawn you
      // can't reach (the Space Center is inside the wall), which is just an invisible
      // wall wearing a nicer hat.
      const southGap = Math.abs(Math.sin(a)) < 0.135 && Math.cos(a) > 0;
      if (southGap) continue;

      const x = Math.sin(a) * WALK_R;
      const z = Math.cos(a) * WALK_R;

      const g = new THREE.Group();
      g.position.set(x, 0, z);
      g.rotation.y = a;
      this.scene.add(g);

      // plinth, baluster, coping rail
      box(1.5, 0.24, 0.5, C.stone, 0, 0.12, 0, g);
      for (const bx of [-0.42, 0, 0.42]) cyl(0.09, 0.13, 0.62, C.stone, bx, 0.55, 0, g, 8);
      const rail = box(1.5, 0.16, 0.44, C.stoneDim, 0, 0.94, 0, g);
      rail.castShadow = true;

      // clipped hedge behind, tinted by season
      const hedge = box(1.6, 1.5, 1.3, this.leaf, 0, 0.75, -1.15, g);
      hedge.castShadow = true; hedge.receiveShadow = true;

      // a stone urn on every eighth pier
      if (i % 8 === 0){
        cyl(0.3, 0.22, 0.5, C.stone, 0, 1.25, 0, g, 10);
        sphere(0.3, this.leaf, 0, 1.62, 0, g, 10);
      }
    }
    // lamps along the balustrade, on the diagonals
    for (let i = 0; i < 8; i++){
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      this._lamp(new THREE.Vector3(Math.sin(a) * (WALK_R - 1.8), 0, Math.cos(a) * (WALK_R - 1.8)));
    }
  }

  // Four clipped parterre gardens, one per quadrant — the manicured look.
  _buildParterres(){
    const QUAD = [[-8.4, -8.4], [8.4, -8.4], [-8.4, 8.4], [8.4, 8.4]];
    QUAD.forEach(([cx, cz]) => {
      const g = new THREE.Group(); g.position.set(cx, 0, cz); this.scene.add(g);

      // a square of lawn, edged with a low clipped hedge
      const bed = new THREE.Mesh(new THREE.PlaneGeometry(5.4, 5.4), new THREE.MeshLambertMaterial({ color: C.grass }));
      bed.rotation.x = -Math.PI/2; bed.position.y = 0.03; bed.receiveShadow = true; g.add(bed);
      for (const [ex, ez, w, d] of [[0, -2.8, 5.8, 0.35], [0, 2.8, 5.8, 0.35], [-2.8, 0, 0.35, 5.8], [2.8, 0, 0.35, 5.8]]){
        const h = box(w, 0.5, d, this.leaf, ex, 0.25, ez, g);
        h.castShadow = true;
      }
      // topiary cones at the corners, a standard rose in the middle
      for (const [tx, tz] of [[-1.9, -1.9], [1.9, -1.9], [-1.9, 1.9], [1.9, 1.9]]){
        cyl(0.001, 0.62, 1.7, this.leaf, tx, 0.85, tz, g, 10).castShadow = true;
      }
      cyl(0.12, 0.12, 1.0, C.cocoa, 0, 0.5, 0, g, 8);
      sphere(0.62, this.leaf, 0, 1.35, 0, g, 12).castShadow = true;
      sphere(0.16, C.raspberry, 0.3, 1.6, 0.24, g, 8);
      sphere(0.16, C.raspberry, -0.34, 1.5, -0.2, g, 8);
    });
  }

  // Three tiers of dressed stone and a gilt finial. It is the posh district, and this
  // is what you look at first.
  //
  // Proportion matters more than parts here: a wide, low basin with a small ornament
  // on top reads as a grey drum with something floating behind it. The basin needs a
  // RIM you can see, and the tiers have to be big enough to join up into one object.
  _buildFountain(){
    const g = new THREE.Group(); this.scene.add(g);

    // basin: a walled pool, not a pancake
    cyl(FOUNTAIN_R, FOUNTAIN_R + 0.15, 1.05, C.stone, 0, 0.52, 0, g, 36).receiveShadow = true;
    cyl(FOUNTAIN_R + 0.14, FOUNTAIN_R + 0.14, 0.16, C.stoneDim, 0, 1.05, 0, g, 36);   // coping
    cyl(FOUNTAIN_R - 0.3, FOUNTAIN_R - 0.3, 0.06, C.breeze, 0, 0.98, 0, g, 36).receiveShadow = true;

    // first stage
    cyl(0.62, 0.85, 1.7, C.stone, 0, 1.9, 0, g, 16);
    cyl(2.05, 2.15, 0.3, C.stone, 0, 2.85, 0, g, 28).castShadow = true;
    cyl(1.75, 1.75, 0.07, C.breeze, 0, 3.02, 0, g, 28);

    // second stage
    cyl(0.34, 0.46, 1.1, C.stone, 0, 3.55, 0, g, 12);
    cyl(1.05, 1.12, 0.24, C.stoneDim, 0, 4.2, 0, g, 22);
    cyl(0.86, 0.86, 0.06, C.breeze, 0, 4.34, 0, g, 22);

    // gilt finial
    cyl(0.16, 0.22, 0.4, C.stone, 0, 4.55, 0, g, 10);
    sphere(0.4, C.tangerine, 0, 4.95, 0, g, 16);
    cyl(0.05, 0.05, 0.5, C.tangerine, 0, 5.4, 0, g, 8);

    // jets arcing from the upper bowl down into the basin
    for (let i = 0; i < 8; i++){
      const a = (i / 8) * Math.PI * 2;
      const jet = cyl(0.06, 0.02, 1.5, C.breeze, Math.sin(a) * 1.5, 3.5, Math.cos(a) * 1.5, g, 6);
      jet.rotation.x = Math.cos(a) * 0.5;
      jet.rotation.z = -Math.sin(a) * 0.5;
    }
  }

  _buildBuilding(b){
    const g = new THREE.Group(); g.position.copy(b.pos); g.lookAt(0, 0, 0); this.scene.add(g);

    const floors = b.tower ? 3 : 1;
    const W = 6.2, D = 4.6, H = 3.6;

    // a stone plinth + steps, so every frontage addresses the plaza properly
    box(W + 1.6, 0.3, D + 2.2, C.stone, 0, 0.15, 0.4, g).receiveShadow = true;
    box(3.0, 0.16, 0.6, C.stoneDim, 0, 0.32, D / 2 + 1.0, g);

    for (let f = 0; f < floors; f++){
      const y = 0.3 + f * H;
      const body = box(W - f * 0.5, H, D - f * 0.35, b.color, 0, y + H / 2, 0, g);
      body.castShadow = true; body.receiveShadow = true;
      // a stone cornice band between storeys — the "expensive" tell
      box(W - f * 0.5 + 0.4, 0.3, D - f * 0.35 + 0.4, C.stone, 0, y + H - 0.05, 0, g);

      // windows: a regular, symmetric bay. Formal means repeated, not scattered.
      for (const wx of [-1.9, 0, 1.9]){
        if (f === 0 && wx === 0) continue;                        // the door sits here
        box(1.0, 1.3, 0.14, C.breeze, wx, y + 1.9, (D - f * 0.35) / 2, g);
        box(1.15, 0.14, 0.16, C.stone, wx, y + 2.62, (D - f * 0.35) / 2, g);   // lintel
      }
    }

    // pedimented doorway with columns
    box(1.5, 2.2, 0.16, C.crustDark, 0, 1.4, D / 2, g);
    for (const px of [-1.15, 1.15]) cyl(0.19, 0.19, 2.5, C.stone, px, 1.55, D / 2 + 0.3, g, 12);
    box(2.9, 0.28, 0.9, C.stone, 0, 2.95, D / 2 + 0.25, g);
    const pediment = cyl(0, 1.55, 0.75, C.stone, 0, 3.4, D / 2 + 0.25, g, 3);
    pediment.rotation.y = Math.PI / 2;

    // roof
    const roofY = 0.3 + floors * H;
    cyl(0.001, W * 0.78, 1.1, C.crustDark, 0, roofY + 0.55, 0, g, 4).castShadow = true;

    const sign = textSprite(b.name, 34, '#3a2e2a', 4.6, 1.0);
    sign.position.set(0, roofY + 1.8, 0.6); g.add(sign);

    // a pair of lamps flanking the steps
    this._lamp(b.pos.clone().multiplyScalar(0.86).add(new THREE.Vector3(2.6, 0, 0)));
    this._lamp(b.pos.clone().multiplyScalar(0.86).add(new THREE.Vector3(-2.6, 0, 0)));
  }

  // The Space Center: the launch pad at the head of the avenue, on the north axis.
  _buildSpacePad(){
    const g = new THREE.Group(); g.position.set(0, 0, -20); this.scene.add(g);

    const apron = new THREE.Mesh(new THREE.CircleGeometry(5.2, 32), new THREE.MeshLambertMaterial({ color: C.stone }));
    apron.rotation.x = -Math.PI/2; apron.position.y = 0.03; apron.receiveShadow = true; g.add(apron);
    cyl(4.4, 4.6, 0.24, C.stoneDim, 0, 0.12, 0, g, 32);

    cyl(0.62, 0.72, 3.0, C.cream, 0, 1.7, 0, g, 20).castShadow = true;
    cyl(0, 0.62, 1.1, 0xC94F4F, 0, 3.75, 0, g, 20);
    for (let i = 0; i < 3; i++){
      const a = i / 3 * Math.PI * 2;
      const f = box(0.14, 0.9, 0.7, 0xC94F4F, Math.cos(a) * 0.68, 0.62, Math.sin(a) * 0.68, g);
      f.rotation.y = -a;
    }
    box(0.5, 0.5, 0.06, C.breeze, 0, 2.5, 0.72, g);           // porthole

    // gantry
    for (const y of [1.2, 2.6]) box(1.9, 0.12, 0.12, C.stoneDim, 1.1, y, 0, g);
    cyl(0.1, 0.1, 3.6, C.stoneDim, 2.0, 1.8, 0, g, 8);

    const sign = textSprite('🚀 Space Center', 26, '#3A2E2A', 4.2, 0.9);
    sign.position.set(0, 5.4, 0); g.add(sign);

    this._lamp(new THREE.Vector3(-5.6, 0, -18));
    this._lamp(new THREE.Vector3( 5.6, 0, -18));
  }

  // Benches, planters and a clipped avenue of trees. Variety from props, not palette.
  _buildStreetFurniture(){
    const bench = (x, z, rot) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = rot; this.scene.add(g);
      for (const bx of [-0.75, 0.75]){
        box(0.16, 0.44, 0.5, C.stoneDim, bx, 0.22, 0, g);
      }
      box(1.9, 0.14, 0.62, C.rye, 0, 0.5, 0, g).castShadow = true;
      box(1.9, 0.5, 0.12, C.rye, 0, 0.78, -0.26, g);
    };
    for (let i = 0; i < 4; i++){
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      bench(Math.sin(a) * 8.6, Math.cos(a) * 8.6, a + Math.PI);
    }

    const planter = (x, z) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      cyl(0.62, 0.5, 0.7, C.stone, 0, 0.35, 0, g, 12).castShadow = true;
      cyl(0.55, 0.55, 0.08, C.cocoa, 0, 0.72, 0, g, 12);
      sphere(0.5, this.leaf, 0, 0.95, 0, g, 12);
      for (let i = 0; i < 4; i++){
        const a = (i / 4) * Math.PI * 2;
        sphere(0.13, i % 2 ? C.raspberry : C.tangerine, Math.cos(a) * 0.34, 1.18, Math.sin(a) * 0.34, g, 8);
      }
    };
    // flanking the avenue, in strict pairs — formal means symmetric
    for (const z of [-11, -5, 5, 11]){ planter(-4.6, z); planter(4.6, z); }

    // A clipped avenue of trees down the road home. Set well back from the avenue: at
    // ±5.4 they stood exactly where the camera sits behind the player and filled half
    // the screen with foliage on arrival.
    for (const z of [15, 19, 23]){
      for (const x of [-8.2, 8.2]){
        const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
        cyl(0.2, 0.26, 1.9, C.rye, 0, 0.95, 0, g, 8).castShadow = true;
        const crown = sphere(1.25, this.leaf, 0, 2.6, 0, g, 12);
        crown.scale.y = 1.15;
        crown.castShadow = true;
      }
    }
  }

  _lamp(pos){
    const g = new THREE.Group();
    g.position.copy(pos);
    this.scene.add(g);
    cyl(0.16, 0.22, 0.36, C.stone, 0, 0.18, 0, g, 10);        // stone base
    cyl(0.07, 0.07, 3.5, C.crustDark, 0, 1.9, 0, g, 8);
    box(0.3, 0.3, 0.3, C.crustDark, 0, 3.72, 0, g);
    const glass = sphere(0.26, LAMP_ON, 0, 3.7, 0, g, 12);
    glass.material = new THREE.MeshBasicMaterial({ color: LAMP_OFF });
    const light = new THREE.PointLight(LAMP_ON, 0, 9, 1.7);
    light.position.set(0, 3.6, 0);
    g.add(light);
    this.lamps.push({ light, glass });
  }

  _updateLamps(){
    const night = isNight();
    this.lamps.forEach(l => {
      l.light.intensity = night ? 2.4 : 0;
      l.glass.material.color.set(night ? LAMP_ON : LAMP_OFF);
    });
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  // Inside the balustrade, and out of the fountain.
  _clampToPlaza(p){
    if (p.length() > WALK_R) p.setLength(WALK_R);
    return p;
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.npcs, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = this._clampToPlaza(new THREE.Vector3(p.x, 0, p.z));
    if (gp.length() < FOUNTAIN_R + 0.5) return;        // don't wade into the fountain
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    const st = getWeatherState();
    HUD.setSubtitle('uptown');
    HUD.say(`Uptown — the Dojo, the Casino, the Upper Crust, the Garage and the Space Center. ${weatherIcon(st.weather)} ${weatherName(st.weather)} · ${st.season}.`);
    HUD.setHint('click to walk · enter the frontages · the Space Center is up the avenue');
    HUD.hideHintSoon(8000);
    this._updateLamps();
  }

  exit(){
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.say('');
    this.panels.forEach(p => p.remove());
    this.panels = [];
  }

  update(dt){
    this.bread.update(dt);
    this._clampToPlaza(this.bread.root.position);
    this.npcs.forEach(n => n.update(dt, this.bread.root.position));

    const night = isNight();
    // this used to hand the ambient system a Vector3 where it wanted 'day' | 'night',
    // so uptown's fireflies never came out and its butterflies never went to bed
    this.ambient.update(dt, night ? 'night' : 'day');
    this.weather.update(dt, this.bread.root.position);
    this._updateLamps();

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        const key = near.label;
        if (this.nearSpot !== key){
          this.nearSpot = key;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){
        this.nearSpot = null;
        HUD.showEnter(null);
      }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
